import { useSignal } from '@preact/signals-react';
import { useEffect, useState } from 'react';
import { FaWhatsapp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import appConfig from '../../../../config/app-config';
import tokenAwareFetcher from '../../../../utils/tokenAwareFetcher';
import communicationApi from '../../../../services/communicationApi';
import MaskEdit from '../../../organisms/MaskEdit';
import TextEdit from '../../../molecules/TextEdit';
import Dialog from '../../../organisms/Dialog';
import TemplateGroupSelector from './TemplateGroupSelector';
import BadgeStatus from '../../../atoms/BadgeStatus';
import NewTooltip from '../../../atoms/NewTooltip';
import { customTemplatePayload, groupsDroppedByCustomMode, groupTemplatePayload, toggleComposition } from './contactPointPayload';
import ShareBranchSelector from './ShareBranchSelector';

const COMMUNICATION_ENV = appConfig.apis.communicationApiEnv;
const BASE_URL = COMMUNICATION_ENV.address;
const API_KEY = COMMUNICATION_ENV.key;
const fetcher = tokenAwareFetcher(BASE_URL, API_KEY);

const PHONE = 'PHONE'

interface ContactPointModalFormProps {
  contactId: string;
  contactPointId: string;
  type: 'PHONE' | 'EMAIL';
  currentValue: string;
  isActive: boolean;
  isPrimary: boolean;
  isWhatsapp?: boolean;
  observation?: string;
  templates?: string[];
  templateGroups?: string[];
  branches?: Array<{ id: string; name: string; document: string; role?: string; city?: string; state?: string }>;
  shared?: boolean;
  sharedContactPointId?: number;
  sharedWith?: Array<{ contactId: string; isActive: boolean }>;
  onClose: () => void;
  onSuccess: (updatedContactPoint: any) => void;
}

export default function ContactPointModalForm({
  contactId,
  contactPointId,
  type,
  currentValue,
  isActive,
  isPrimary,
  isWhatsapp = false,
  observation = '',
  templates = [],
  templateGroups: initialTemplateGroups = [],
  branches = [],
  shared = false,
  sharedContactPointId,
  sharedWith: initialSharedWith = [],
  onClose,
  onSuccess,
}: ContactPointModalFormProps) {
  const isSending = useSignal(false);
  const formData = useSignal({
    value: currentValue,
    isActive,
    isPrimary,
    isWhatsapp,
    notes: observation,
  });
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>(templates);
  const [phoneType, setPhoneType] = useState<'mobile' | 'landline'>('mobile');
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const availableCompositions = useSignal<
    Array<{ id: string; name: string; description: string; channel: string; groupName?: string | null }>
  >([]);
  const [groupNames, setGroupNames] = useState<string[]>([]);
  const [groupUuidByName, setGroupUuidByName] = useState<Record<string, string>>({});
  const [groupsLoadFailed, setGroupsLoadFailed] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState<string[]>(initialTemplateGroups);
  const [customMode, setCustomMode] = useState(initialTemplateGroups.length === 0 && templates.length > 0);
  const [shareEnabled, setShareEnabled] = useState(shared || false);
  const [selectedBranches, setSelectedBranches] = useState<Set<string>>(
    () => new Set(initialSharedWith.filter(s => s.isActive).map(s => s.contactId))
  );

  // Canal elegível para este contact point: e-mail recebe composições EMAIL; telefone com
  // WhatsApp recebe EXTERNAL_MESSAGE. Impede escolher composição de um canal incompatível.
  const targetChannel: string | null =
    type === 'EMAIL' ? 'EMAIL' : formData.value.isWhatsapp ? 'EXTERNAL_MESSAGE' : null;

  const compositionsForChannel = () =>
    availableCompositions.value.filter((c) => targetChannel !== null && c.channel === targetChannel);

  useEffect(() => {
    // Sincronizar com props quando mudarem
    formData.value = {
      value: currentValue,
      isActive,
      isPrimary,
      isWhatsapp,
      notes: observation,
    };
    setSelectedTemplates(templates);
  }, [currentValue, isActive, isPrimary, isWhatsapp, observation, templates, formData]);

  useEffect(() => {
    const fetchData = async () => {
      setLoadingTemplates(true);
      try {
        const groupsSentinel = Symbol('groups-failed');
        const [metaPage, groupsResult] = await Promise.all([
          communicationApi.listMessageCompositions({ pageSize: 9999 }).catch(() => ({ list: [] })),
          communicationApi.listGroups({}).catch(() => groupsSentinel),
        ]);

        availableCompositions.value = (metaPage.list || [])
          .filter((m) => m.status === 'ACTIVE')
          .map((m) => ({
            id: m.id,
            name: m.name,
            description: m.description,
            channel: m.channel,
            groupName: m.groupName ?? null,
          }));

        if (groupsResult === groupsSentinel) {
          setGroupsLoadFailed(true);
          return;
        }

        const groups = groupsResult as Awaited<ReturnType<typeof communicationApi.listGroups>>;
        const activeGroups = groups.filter((g) => g.status === 'ACTIVE');
        const linkedInactive = groups.filter(
          (g) => g.status !== 'ACTIVE' && initialTemplateGroups.includes(g.name),
        );
        setGroupNames([...activeGroups, ...linkedInactive].map((g) => g.name));
        const nameToUuid = Object.fromEntries(activeGroups.map((g) => [g.name, g.id]));
        for (const g of groups) {
          if (initialTemplateGroups.includes(g.name) && !(g.name in nameToUuid)) {
            nameToUuid[g.name] = g.id;
          }
        }
        setGroupUuidByName(nameToUuid);

        const isCreating = !contactPointId;
        if (isCreating && initialTemplateGroups.length === 0) {
          const defaults = activeGroups.filter((g) => g.defaultForNewContactPoints).map((g) => g.name);
          if (defaults.length > 0) setSelectedGroups(defaults);
        }
      } catch (error) {
        console.error('Erro ao carregar composições:', error);
      } finally {
        setLoadingTemplates(false);
      }
    };
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getTemplatePayload = (forUpdate = false) => {
    if (customMode) {
      return customTemplatePayload({
        forUpdate,
        selectedTemplates,
        initialTemplates: templates,
        initialGroups: initialTemplateGroups,
        compositions: availableCompositions.value,
      });
    }
    return groupTemplatePayload({
      forUpdate,
      groupsLoadFailed,
      selectedGroups,
      initialGroups: initialTemplateGroups,
      groupUuidByName,
    });
  };

  const buildPayload = () => {
    const isCreating = !contactPointId;
    const base: any = {
      type,
      value: formData.value.value,
      isActive: formData.value.isActive,
      isPrimary: formData.value.isPrimary,
      notes: formData.value.notes,
      ...(type === PHONE && { isWhatsapp: formData.value.isWhatsapp }),
      ...getTemplatePayload(),
    };

    if (isCreating) return base;

    const payload: any = {
      ...(formData.value.value !== currentValue && { value: formData.value.value }),
      ...(formData.value.isActive !== isActive && { isActive: formData.value.isActive }),
      ...(formData.value.isPrimary !== isPrimary && { isPrimary: formData.value.isPrimary }),
      ...(type === PHONE && { isWhatsapp: formData.value.isWhatsapp }),
      ...(formData.value.notes !== observation && { notes: formData.value.notes }),
      ...getTemplatePayload(true),
      ...(shared && shareEnabled && { sharedWith: Array.from(selectedBranches) }),
    };
    return payload;
  };

  const submitContactPoint = async (isCreating: boolean, payload: any) => {
    // If editing a shared contact point, use the shared endpoint
    const path = !isCreating && shared && sharedContactPointId
      ? `/contacts/${contactId}/shared-contact-points/${sharedContactPointId}`
      : isCreating
        ? `/contacts/${contactId}/contact-points`
        : `/contacts/${contactId}/contact-points/${contactPointId}`;

    const response = isCreating
      ? await fetcher.post(path, payload)
      : await fetcher.patch(path, payload);

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || `Erro ao ${isCreating ? 'criar' : 'atualizar'} contato`);
    }
    return response.json();
  };

  const shareAfterCreate = async (cpId: string, authHeaders: Record<string, string>, result: any) => {
    const shareResult = await fetcher.post(`/contacts/${contactId}/contact-points/${cpId}/share`, { sharedWith: Array.from(selectedBranches) });
    if (!shareResult.ok) throw new Error('Share request failed');
    const shareData = await shareResult.json();
    const sharedMembers = Array.from(selectedBranches).map(id => ({ contactId: id, isActive: true }));
    onSuccess({
      ...result, id: shareData.contactPointId || cpId, replacesId: cpId,
      shared: true, isOwner: true, isEditable: true,
      sharedContactPointId: shareData.sharedContactPointId, sharedWith: sharedMembers,
    });
  };

  const unshareExisting = async (authHeaders: Record<string, string>, result: any) => {
    const response = await fetcher.delete(`/contacts/${contactId}/shared-contact-points/${sharedContactPointId}`);
    if (!response.ok) throw new Error('Unshare request failed');
    onSuccess({ ...result, shared: false, isOwner: false, isEditable: true, sharedContactPointId: undefined, sharedWith: [] });
  };

  const handleSubmit = async () => {
    if (!formData.value.value.trim()) {
      toast.error('Valor é obrigatório');
      return;
    }

    isSending.value = true;
    const isCreating = !contactPointId;

    try {
      const result = await submitContactPoint(isCreating, buildPayload());
      const authHeaders = {};

      const wantsShare = shareEnabled && selectedBranches.size > 0;

      if (isCreating && wantsShare && result.id) {
        try {
          await shareAfterCreate(result.id, authHeaders, result);
          toast.success('Contato criado e compartilhado com sucesso!');
          onClose();
          return;
        } catch { toast.warning('Contato criado, mas erro ao compartilhar'); }
      }

      if (!isCreating && !shared && wantsShare) {
        try {
          await shareAfterCreate(contactPointId, authHeaders, result);
          toast.success('Contato compartilhado com sucesso!');
          onClose();
          return;
        } catch { toast.warning('Contato atualizado, mas erro ao compartilhar'); }
      }

      if (!isCreating && shared && sharedContactPointId) {
        if (!shareEnabled) {
          await unshareExisting(authHeaders, result);
          toast.success('Contato descompartilhado com sucesso!');
        } else {
          const sharedMembers = Array.from(selectedBranches).map(id => ({ contactId: id, isActive: true }));
          onSuccess({ ...result, shared: true, isOwner: true, isEditable: true, sharedContactPointId, sharedWith: sharedMembers });
          toast.success('Contato atualizado com sucesso!');
        }
        onClose();
        return;
      }

      toast.success(`Contato ${isCreating ? 'criado' : 'atualizado'} com sucesso!`);
      onSuccess(result);
      onClose();
    } catch (error: any) {
      toast.error(error.message || `Erro ao ${isCreating ? 'criar' : 'atualizar'} contato`);
    } finally {
      isSending.value = false;
    }
  };

  const toggleTemplate = (compositionId: string) => {
    const composition = availableCompositions.value.find((c) => c.id === compositionId) ?? { id: compositionId, name: compositionId };
    setSelectedTemplates((prev) => toggleComposition(prev, composition));
  };

  const droppedGroups = groupsDroppedByCustomMode({
    customMode,
    forUpdate: Boolean(contactPointId),
    selectedGroups,
    initialGroups: initialTemplateGroups,
  });

  const toggleGroup = (group: string) => {
    setSelectedGroups(prev =>
      prev.includes(group) ? prev.filter(g => g !== group) : [...prev, group]
    );
  };

  return (
    <Dialog
      id="contact-point-modal"
      show={true}
      size="lg"
      onClose={onClose}
      header={`${contactPointId ? 'Editar' : 'Novo'} ${type === PHONE ? 'Telefone' : 'Email'}`}
      body={
        <div className="p-6 space-y-4">
          {/* Two columns layout */}
          <div className="grid grid-cols-3 gap-6">
            {/* Left column - 2/3 width */}
            <div className="col-span-2 space-y-4">
              {/* Value */}
              {type === PHONE ? (
                <MaskEdit
                  key={phoneType}
                  label="Telefone"
                  mask={phoneType === 'mobile' ? '(__) _____-____' : '(__) ____-____'}
                  replacement={{ _: /\d/ }}
                  value={formData.value.value}
                  onChange={(e) => (formData.value = { ...formData.value, value: e.target.value })}
                  placeholder=""
                  prefix={
                    <div className="flex gap-1 p-0.5 bg-gray-100 dark:bg-slate-700 rounded-md mr-2">
                      <button type="button" onClick={() => setPhoneType('mobile')} className={`px-2 py-1 text-xs rounded transition-colors ${phoneType === 'mobile' ? 'bg-white dark:bg-slate-600 text-gray-900 dark:text-white shadow-sm font-medium' : 'text-gray-500 dark:text-gray-400'}`}>Celular</button>
                      <button type="button" onClick={() => setPhoneType('landline')} className={`px-2 py-1 text-xs rounded transition-colors ${phoneType === 'landline' ? 'bg-white dark:bg-slate-600 text-gray-900 dark:text-white shadow-sm font-medium' : 'text-gray-500 dark:text-gray-400'}`}>Fixo</button>
                    </div>
                  }
                />
              ) : (
                <TextEdit
                  label="Email"
                  value={formData.value.value}
                  onChange={(e) => (formData.value = { ...formData.value, value: e.target.value })}
                  placeholder=""
                />
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Observação
                </label>
                <textarea
                  value={formData.value.notes}
                  onChange={(e) => (formData.value = { ...formData.value, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 dark:bg-slate-700 dark:text-white resize-none"
                  placeholder=""
                />
              </div>
            </div>

            {/* Right column - Toggles */}
            <div className="space-y-4">
              {/* WhatsApp (apenas para telefone) */}
              {type === PHONE && (
                <label className="flex items-center gap-3 cursor-pointer">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={formData.value.isWhatsapp}
                      onChange={(e) => (formData.value = { ...formData.value, isWhatsapp: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 dark:bg-slate-600 rounded-full peer peer-checked:bg-green-500 peer-focus:ring-2 peer-focus:ring-green-300 transition-colors"></div>
                    <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5"></div>
                  </div>
                  <span className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <FaWhatsapp className="text-green-500" />
                    WhatsApp
                  </span>
                </label>
              )}

              {/* Primary */}
              <label className="flex items-center gap-3 cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={formData.value.isPrimary}
                    onChange={(e) => (formData.value = { ...formData.value, isPrimary: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-slate-600 rounded-full peer peer-checked:bg-blue-500 peer-focus:ring-2 peer-focus:ring-blue-300 transition-colors"></div>
                  <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5"></div>
                </div>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Principal
                </span>
              </label>

              {/* Active */}
              <label className="flex items-center gap-3 cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={formData.value.isActive}
                    onChange={(e) => (formData.value = { ...formData.value, isActive: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-slate-600 rounded-full peer peer-checked:bg-blue-500 peer-focus:ring-2 peer-focus:ring-blue-300 transition-colors"></div>
                  <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5"></div>
                </div>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Ativo
                </span>
              </label>
            </div>
          </div>

          {/* Composições de notificação por grupo ou personalizadas */}
          <TemplateGroupSelector
            groupNames={groupNames}
            selectedGroups={selectedGroups}
            onToggleGroup={toggleGroup}
            availableCompositions={compositionsForChannel()}
            selectedTemplates={selectedTemplates}
            onToggleTemplate={toggleTemplate}
            customMode={customMode}
            onToggleMode={() => setCustomMode(!customMode)}
            loading={loadingTemplates}
            channelAvailable={targetChannel !== null}
          />
          {droppedGroups.length > 0 && (
            <NewTooltip content={droppedGroups.join(', ')}>
              <BadgeStatus color="orange" text="Ao salvar, os grupos deixam de valer para este contato" />
            </NewTooltip>
          )}

          {branches.length > 0 && (
            <ShareBranchSelector
              branches={branches}
              shareEnabled={shareEnabled}
              selectedBranches={selectedBranches}
              onToggleShare={(enabled) => { setShareEnabled(enabled); if (!enabled) setSelectedBranches(new Set()); }}
              onToggleBranch={(branchId) => {
                const next = new Set(selectedBranches);
                next.has(branchId) ? next.delete(branchId) : next.add(branchId);
                setSelectedBranches(next);
              }}
              onToggleAll={() => {
                setSelectedBranches(selectedBranches.size === branches.length ? new Set() : new Set(branches.map(b => b.id)));
              }}
            />
          )}
        </div>
      }
      footer={
        <div className="flex gap-3">
          <button onClick={onClose} disabled={isSending.value} className="px-4 py-2 bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-slate-600">Cancelar</button>
          <button onClick={handleSubmit} disabled={isSending.value || (shareEnabled && selectedBranches.size === 0)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {isSending.value ? (contactPointId ? 'Atualizando...' : 'Salvando...') : (contactPointId ? 'Atualizar' : 'Salvar')}
          </button>
        </div>
      }
    />
  );
}
